import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ChevronLeft, Loader2, Download, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { api } from "../../utils/api";
import { toast } from "sonner";
import AccessRestricted from "../../components/AccessRestricted";

interface LeaveDigestEntry {
  leaveType: string;
  hours: number;
  startDate: string;
  endDate: string;
  reason: string;
  status: "Approved" | "Pending" | "Reported by Manager";
}

interface LeaveDigestEmployee {
  id: string;
  name: string;
  annualRemaining: string;
  personalRemaining: string;
  totalHours: number;
  entries: LeaveDigestEntry[];
}

interface DigestRange {
  startDate: string;
  endDate: string;
}

// Number of business days the payroll digest covers by default.
const DIGEST_BUSINESS_DAYS = 5;

// Attendance runs on Sydney time, so "today" must be anchored there too.
const getSydneyToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });

/**
 * The digest defaults to the five most recent business days before today —
 * the window payroll reviews when it is run on a Wednesday.
 */
function getDefaultDigestRange(): DigestRange {
  const cursor = new Date(`${getSydneyToday()}T00:00:00Z`);
  const days: string[] = [];
  while (days.length < DIGEST_BUSINESS_DAYS) {
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    const dayOfWeek = cursor.getUTCDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push(cursor.toISOString().slice(0, 10));
    }
  }
  return { startDate: days[days.length - 1], endDate: days[0] };
}

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const DIGEST_STATUS_STYLES: Record<LeaveDigestEntry["status"], string> = {
  "Approved": "bg-green-50 text-green-700 border-green-100",
  "Pending": "bg-amber-50 text-amber-700 border-amber-100",
  "Reported by Manager": "bg-blue-50 text-blue-700 border-blue-100",
};

const LeaveDigest = () => {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  // The digest reports the same population as the Leave Balances tab.
  const canView = ability.can(ACTIONS.MANAGE, RESOURCES.LEAVE_BALANCES_TAB);

  const [range, setRange] = useState<DigestRange>(getDefaultDigestRange);
  const [employees, setEmployees] = useState<LeaveDigestEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const invalidRange = Boolean(range.startDate && range.endDate && range.endDate < range.startDate);

  useEffect(() => {
    if (!canView) return;
    if (!range.startDate || !range.endDate || invalidRange) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    api.get<{ success: boolean; data: { employees: LeaveDigestEmployee[] } }>(
      `/api/attendance/leave-digest?startDate=${range.startDate}&endDate=${range.endDate}`,
      { skipErrorToast: true }
    )
      .then((res) => {
        if (cancelled) return;
        setEmployees(res?.data?.employees ?? []);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("Error building leave digest:", error);
        setEmployees([]);
        setFailed(true);
        toast.error(error instanceof Error ? error.message : "Failed to build the leave digest");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [canView, range.startDate, range.endDate, invalidRange]);

  const entryCount = employees.reduce((sum, emp) => sum + emp.entries.length, 0);
  const totalHours = employees.reduce((sum, emp) => sum + emp.totalHours, 0);

  const handleDownload = () => {
    if (entryCount === 0) return;

    const headers = [
      "Employee", "Leave Type", "From", "To", "Hours", "Reason", "Approval Status",
      "Annual Leave Remaining (h)", "Personal Leave Remaining (h)"
    ];
    // Grouped by individual: every row repeats the employee's remaining balances
    // so the file stands alone once it reaches payroll.
    const rows: string[] = [headers.map(csvCell).join(",")];

    employees.forEach((emp) => {
      emp.entries.forEach((entry) => {
        rows.push([
          emp.name,
          entry.leaveType,
          entry.startDate,
          entry.endDate,
          entry.hours,
          entry.reason || "",
          entry.status,
          emp.annualRemaining,
          emp.personalRemaining
        ].map(csvCell).join(","));
      });
    });

    // BOM so Excel opens the file as UTF-8.
    const blob = new Blob([`\uFEFF${rows.join("\r\n")}`], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `leave_digest_${range.startDate}_to_${range.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <AccessRestricted
        accessMessage="You don't have permission to view the leave digest report."
        backMessage="Back to HR Admin"
        navigateTo="/attendance/admin"
      />
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="icon"
          className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
          onClick={() => navigate("/attendance/admin")}
        >
          <ChevronLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Leave Digest Report</h1>
          <p className="text-gray-500 font-medium text-sm">
            All leave taken in the selected period, grouped by employee — for payroll review
          </p>
        </div>
        <Button
          onClick={handleDownload}
          disabled={loading || entryCount === 0 || invalidRange}
          className="rounded-xl bg-black text-white hover:bg-gray-800 h-10 px-4 font-semibold flex items-center gap-2 shrink-0 whitespace-nowrap"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download CSV
        </Button>
      </div>

      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-bold text-gray-900">From</label>
              <Input
                type="date"
                value={range.startDate}
                onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
              />
            </div>
            <div className="space-y-2 flex-1">
              <label className="text-sm font-bold text-gray-900">To</label>
              <Input
                type="date"
                value={range.endDate}
                onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="rounded-xl border-gray-200 bg-gray-50/50 h-11 focus:bg-white text-gray-900"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setRange(getDefaultDigestRange())}
              className="rounded-xl border-gray-200 bg-white hover:bg-gray-50 h-11 px-4 font-semibold text-gray-700 flex items-center gap-2 shrink-0"
            >
              <RotateCcw className="h-4 w-4" />
              Last 5 business days
            </Button>
          </div>
          <p className="text-xs text-gray-500 font-medium mt-4">
            Defaults to the last 5 business days — run it on Wednesday to review the prior pay period.
            Leave spanning the period edges is pro-rated to the days inside it. Pending requests and leave reported by a
            manager are included; rejected and cancelled requests are not.
          </p>
        </div>

        {/* Report */}
        <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-bold text-gray-900 text-lg">
                {range.startDate} to {range.endDate}
              </h3>
              <p className="text-gray-500 text-sm font-medium">
                {invalidRange
                  ? "The end date must be on or after the start date."
                  : loading
                    ? "Building report..."
                    : `${employees.length} ${employees.length === 1 ? "employee" : "employees"} · ${entryCount} ${entryCount === 1 ? "entry" : "entries"} · ${totalHours} hours`}
              </p>
            </div>
            {loading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-900 font-bold bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Leave Type</th>
                  <th className="px-6 py-4">From</th>
                  <th className="px-6 py-4">To</th>
                  <th className="px-6 py-4 text-right">Hours</th>
                  <th className="px-6 py-4">Reason</th>
                  <th className="px-6 py-4">Approval Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500 font-medium">Loading digest...</p>
                    </td>
                  </tr>
                ) : entryCount === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500 italic">
                      {invalidRange
                        ? "Adjust the dates to build the report."
                        : failed
                          ? "The digest could not be loaded."
                          : "No leave was taken in this period."}
                    </td>
                  </tr>
                ) : (
                  employees.map((emp) => (
                    <React.Fragment key={emp.id}>
                      {emp.entries.map((entry, idx) => (
                        <tr key={`${emp.id}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                          {idx === 0 && (
                            <td rowSpan={emp.entries.length} className="px-6 py-4 align-top border-r border-gray-50">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8 text-xs font-medium border border-gray-100 shadow-sm">
                                  <AvatarFallback className="bg-gray-50 text-gray-600">
                                    {emp.name.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="text-gray-900 font-medium whitespace-nowrap">{emp.name}</p>
                                  <p className="text-xs text-gray-500 font-medium whitespace-nowrap">
                                    Annual {emp.annualRemaining} h · Personal {emp.personalRemaining} h remaining
                                  </p>
                                </div>
                              </div>
                            </td>
                          )}
                          <td className="px-6 py-4 font-medium text-gray-900">{entry.leaveType}</td>
                          <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{entry.startDate}</td>
                          <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{entry.endDate}</td>
                          <td className="px-6 py-4 text-right font-bold text-gray-900">{entry.hours}</td>
                          <td className="px-6 py-4 text-gray-600">{entry.reason || <span className="text-gray-400">—</span>}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border whitespace-nowrap ${DIGEST_STATUS_STYLES[entry.status]}`}>
                              {entry.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50/50">
                        <td colSpan={7} className="px-6 py-2 text-right text-xs font-bold text-gray-600">
                          {emp.name} total: {emp.totalHours} hours
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveDigest;
